<template>
  <div class="coupon_page coupon_list_page">
    <div class="coupon_page_header">
      <navbar title="優惠券" :back-route="memberPageRoute"></navbar>
      <div class="tabs_container">
        <div class="tab" v-for="(item, index) in tabs" :key="index">
          <button @click="changeTab(index)" :class="{
            button_selected: index === currentTab,
            button_unselected: index !== currentTab,
          }">
            {{ item }}
          </button>
        </div>
      </div>
    </div>

    <!-- 未領取：後端已篩選目前可領的母券 -->
    <div class="coupon_list" v-if="currentTab == 0">
      <couponLoading v-if="couponTemplates.loading" />
      <div class="coupon_list_hint" v-else-if="couponTemplates.error">
        <p>{{ couponTemplates.error }}</p>
        <button type="button" class="retry_button" @click="fetchTemplates(templatePage)">重新載入</button>
      </div>
      <p class="coupon_list_hint" v-else-if="couponTemplates.items.length === 0">目前沒有可領取的優惠券</p>
      <template v-else>
        <publicCouponTemplate v-for="item in couponTemplates.items" :key="`${item.template_uuid}|${item.offer_uuid}`"
          :item="item" @showDetail="toClaimCouponPage"></publicCouponTemplate>
        <div class="coupon_pager" v-if="getTemplatePageCount > 1">
          <button type="button" :disabled="couponTemplates.page <= 1"
            @click="fetchTemplates(couponTemplates.page - 1)">上一頁</button>
          <span>{{ couponTemplates.page }} / {{ getTemplatePageCount }}</span>
          <button type="button" :disabled="couponTemplates.page >= getTemplatePageCount"
            @click="fetchTemplates(couponTemplates.page + 1)">下一頁</button>
        </div>
        <p class="coupon_list_footer">目前共 {{ couponTemplates.total }} 項可領優惠</p>
      </template>
    </div>

    <!-- 已領取：子券倉庫，所有狀態都列出 -->
    <div class="coupon_list" v-if="currentTab == 1">
      <couponLoading v-if="memberCoupons.loading" />
      <div class="coupon_list_hint" v-else-if="memberCoupons.error">
        <p>{{ memberCoupons.error }}</p>
        <button type="button" class="retry_button" @click="fetchMemberCoupons(memberCouponPage)">重新載入</button>
      </div>
      <p class="coupon_list_hint" v-else-if="memberCoupons.items.length === 0">目前尚無已領取的優惠券</p>
      <template v-else>
        <unusedCoupon v-for="item in memberCoupons.items" :key="item.member_coupon_uuid" :item="item" :nowMs="nowMs"
          @showMemberDetail="toUseCouponPage"></unusedCoupon>
        <div class="coupon_pager" v-if="getMemberCouponPageCount > 1">
          <button type="button" :disabled="memberCoupons.page <= 1"
            @click="fetchMemberCoupons(memberCoupons.page - 1)">上一頁</button>
          <span>{{ memberCoupons.page }} / {{ getMemberCouponPageCount }}</span>
          <button type="button" :disabled="memberCoupons.page >= getMemberCouponPageCount"
            @click="fetchMemberCoupons(memberCoupons.page + 1)">下一頁</button>
        </div>
        <p class="coupon_list_footer">目前共 {{ memberCoupons.total }} 張優惠券</p>
      </template>
    </div>
  </div>
</template>
<script>
import publicCouponTemplate from "@/components/coupon/publicCouponTemplate.vue";
import unusedCoupon from "@/components/coupon/unusedCoupon.vue";
import couponLoading from "@/components/coupon/couponLoading.vue";
import navbar from "@/components/general/navbar.vue"
import { mapGetters } from 'vuex'
import { getCouponValidFromAt, getCouponValidUntilAt } from "@/utils/coupon.utils";

// setTimeout 上限約 24.8 天，長效期分段計時。
const MAX_TIMER_DELAY = 2147483647;

export default {
  components: {
    navbar,
    publicCouponTemplate,
    unusedCoupon,
    couponLoading,
  },
  data() {
    return {
      tabs: ["未領取", "已領取"],
      currentTab: 0,
      templatePage: 1,
      memberCouponPage: 1,
      // 卡片依此判斷是否已生效；重查、焦點恢復與生效邊界時更新
      nowMs: Date.now(),
      validFromTimer: null,
      isPageActive: true,
    };
  },
  computed: {
    storeCode() {
      return this.$store.getters['userData/storeCode'];
    },
    // 返回一律回會員 VIP 卡，不依瀏覽紀錄回上一頁
    memberPageRoute() {
      return {
        name: 'memberInfo',
        params: {
          store: this.storeCode || this.$route.params.store,
        },
      };
    },
    ...mapGetters('coupon', {
      couponTemplates: 'couponTemplates',
      memberCoupons: 'memberCouponsV2',
    }),
    getTemplatePageCount() {
      return Math.ceil(this.couponTemplates.total / (this.couponTemplates.pageSize || 20));
    },
    getMemberCouponPageCount() {
      return Math.ceil(this.memberCoupons.total / (this.memberCoupons.pageSize || 20));
    },
  },
  mounted() {
    this.initialize();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  },
  beforeUnmount() {
    this.isPageActive = false;
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    clearTimeout(this.validFromTimer);
  },
  methods: {
    initialize() {
      if (this.$route.query.tab === 'claimed') {
        this.currentTab = 1;
      }
      this.fetchTemplates(1);
      this.fetchMemberCoupons(1);
    },
    changeTab(index) {
      this.currentTab = index;
    },
    fetchTemplates(page) {
      this.templatePage = page;
      return this.$store.dispatch('coupon/fetchCouponTemplates', { page });
    },
    async fetchMemberCoupons(page) {
      this.memberCouponPage = page;
      await this.$store.dispatch('coupon/fetchMemberCouponsV2', { page });
      if (!this.isPageActive) return;
      this.nowMs = Date.now();
      this.scheduleValidFromUpdate();
    },
    // 實際生效／到期邊界更新畫面並重查 API 狀態。
    scheduleValidFromUpdate() {
      clearTimeout(this.validFromTimer);
      if (!this.isPageActive) return;
      const upcoming = this.memberCoupons.items
        .filter((item) => item.status === "available")
        .flatMap((item) => [getCouponValidFromAt(item), getCouponValidUntilAt(item)])
        .map((value) => Date.parse(value))
        .filter((ms) => Number.isFinite(ms) && ms > this.nowMs);
      if (upcoming.length === 0) return;
      const nextBoundary = Math.min(...upcoming);
      const delay = Math.min(nextBoundary - this.nowMs, MAX_TIMER_DELAY);
      this.validFromTimer = setTimeout(() => {
        this.nowMs = Date.now();
        if (this.nowMs >= nextBoundary) {
          this.fetchMemberCoupons(this.memberCouponPage);
        } else {
          this.scheduleValidFromUpdate();
        }
      }, delay);
    },
    onVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      this.nowMs = Date.now();
      if (this.currentTab === 1) {
        this.fetchMemberCoupons(this.memberCouponPage);
      } else {
        this.fetchTemplates(this.templatePage);
      }
    },
    // 只進母券詳情，不在列表直接領取
    toClaimCouponPage(templateUuid) {
      this.$router.push({
        path: `/${this.storeCode}/coupon/claim/${templateUuid}`,
      });
    },
    // 只進子券詳情，QR 由詳情頁重查狀態後直接顯示
    toUseCouponPage(memberCouponUuid) {
      this.$router.push({
        path: `/${this.storeCode}/coupon/use/${memberCouponUuid}`,
      });
    },
  },
  beforeRouteEnter(to, from, next) {
    /*
      如果從使用 coupon 頁面返回
      則將 currentTab 設為 1（已領取）
    */
    if (from.params.method === "use") {
      next((vm) => {
        vm.currentTab = 1;
      });
    } else {
      next();
    }
  },
};
</script>
<style lang="scss">
.coupon_page {
  width: 100vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #F0F0F0;

  h2 {
    font-family: PingFang SC;
    font-size: 26px;
    font-weight: 500;
    color: #FD5001;
  }
}

.coupon_list_page {
  width: 100%;

  .coupon_page_header {
    position: sticky;
    top: 0;
    z-index: 100;
    width: 100%;
    flex-shrink: 0;
    background-color: #F0F0F0;
  }

  .tabs_container {
    width: 100%;
  }
}

.tabs_container {
  width: 100vw;
  height: 50px;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-around;
  box-shadow: 0px 4px 5px 0px rgba(112, 112, 112, 0.33);
  -webkit-box-shadow: 0px 4px 5px 0px rgba(112, 112, 112, 0.33);
  -moz-box-shadow: 0px 4px 5px 0px rgba(112, 112, 112, 0.33);
  margin-bottom: 2px;

  .tab {
    flex-grow: 1;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 0;
  }

  .button_unselected {
    width: 100%;
    border: none;
    height: 100%;
    padding: 0 4px;
    text-align: center;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 18px;
    font-weight: 600;
    color: #666666;
    background-color: #fff;
    border-bottom: 3px solid transparent;
    border-radius: 0;
  }

  .button_selected {
    width: 100%;
    border: none;
    height: 100%;
    padding: 0 4px;
    text-align: center;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 18px;
    font-weight: 600;
    background-color: #fff;
    color: #FD5001;
    border-bottom: 3px solid #FD5001;
    border-radius: 0;
  }
}


.coupon {
  width: 100%;
  padding: 15px 2.5vw;
  height: 188px;
  background-color: #fff;
  border-bottom: dotted 1px #999999;
  margin-bottom: 10px;

  .claim_button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 120px;
    height: 36px;
    background-color: #FBCB02;
    border-radius: 6px;
    border: none;
    color: #000;
    font-weight: 500;
    font-family: PingFang SC;
    font-size: 15px;
    flex-shrink: 0;
    margin-top: 5px;
  }

  .coupon_image {
    width: 120px;
    height: 100%;
    border-radius: 6px;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 120px;
      border-radius: 6px;
      flex-shrink: 0;
    }
  }
}

.coupon_detail {
  flex-grow: 1;
  height: 100%;
  margin-left: 10px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;

  .coupon_title {
    font-family: PingFang SC;
    font-size: 18px;
    font-weight: 500;
    color: #000;
    margin: 0 0 8px;
  }

  .coupon_descrption {
    font-size: 16px;
    font-weight: 400;
    font-family: PingFang SC;
    color: #666666;
    width: 100%;
  }

  .event_duration {
    font-family: PingFang SC;
    font-size: 12px;
    white-space: nowrap;
    font-weight: 400;
    color: #666666;
    margin: 0;
  }

  .usage_duration {
    font-family: PingFang SC;
    font-size: 12px;
    font-weight: 400;
    color: #fa821b;
    margin: 0;
    width: 100%;
  }
}

.coupon_list {
  width: 100%;
  padding-bottom: 20px;
}

.coupon_list_hint {
  margin: 40px 16px;
  text-align: center;
  color: #666666;
  font-size: 15px;

  p {
    margin: 0 0 12px;
  }
}

.retry_button,
.coupon_pager button {
  padding: 8px 18px;
  border: none;
  border-radius: 6px;
  background-color: #FBCB02;
  color: #000;
  font-size: 14px;
  font-weight: 600;

  &:disabled {
    background-color: #ddd;
    color: #777;
  }
}

.coupon_pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin: 8px 0;
  font-size: 14px;
  color: #666666;
}

.coupon_list_footer {
  margin: 12px 0;
  text-align: center;
  font-size: 12px;
  color: #8a8a8a;
}
</style>
